import React, {useEffect, useState} from "react";
import {TIMEZONES, getCurrentTime, getCurrentDate} from "./timezones";
import {PayToUnlock} from "pay/dist/pay/PayToUnlock";

const TimezoneRow = ({tz, now}: {tz: typeof TIMEZONES[0]; now: Date}) => {
  const time = getCurrentTime(tz.timezone);
  const date = getCurrentDate(tz.timezone);

  return (
    <tr style={{borderBottom: "1px solid #eee"}}>
      <td style={{padding: "10px 12px", fontWeight: 500}}>{tz.country}</td>
      <td style={{padding: "10px 12px", color: "#555"}}>{tz.city}</td>
      <td style={{padding: "10px 12px", fontFamily: "monospace", fontSize: "15px", color: "#667eea", fontWeight: 600}}>{time}</td>
      <td style={{padding: "10px 12px", color: "#888", fontSize: "13px"}}>{date}</td>
      <td style={{padding: "10px 12px", color: "#aaa", fontSize: "13px"}}>{tz.utcOffset}</td>
    </tr>
  );
};

const TimezoneTable = ({timezones}: {timezones: typeof TIMEZONES}) => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div>
      <div style={{marginBottom: "16px", color: "#999", fontSize: "14px"}}>
        Auto-refreshing every second · {timezones.length} timezones · Updated: {now.toLocaleTimeString()}
      </div>
      <div style={{overflowX: "auto"}}>
        <table style={{width: "100%", borderCollapse: "collapse", fontSize: "14px"}}>
          <thead>
            <tr style={{background: "#f8f9fa", borderBottom: "2px solid #dee2e6"}}>
              <th style={{padding: "12px", textAlign: "left", fontWeight: 600}}>Country</th>
              <th style={{padding: "12px", textAlign: "left", fontWeight: 600}}>City</th>
              <th style={{padding: "12px", textAlign: "left", fontWeight: 600}}>Current Time</th>
              <th style={{padding: "12px", textAlign: "left", fontWeight: 600}}>Date</th>
              <th style={{padding: "12px", textAlign: "left", fontWeight: 600}}>UTC Offset</th>
            </tr>
          </thead>
          <tbody>
            {timezones.map((tz) => (
              <TimezoneRow key={tz.timezone} tz={tz} now={now} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const App = () => {
  const [search, setSearch] = useState("");

  const filtered = TIMEZONES.filter((tz) => {
    const q = search.toLowerCase();
    return !q || tz.country.toLowerCase().includes(q) || tz.city.toLowerCase().includes(q) || tz.timezone.toLowerCase().includes(q);
  });

  return (
    <div style={{maxWidth: "960px", margin: "0 auto", padding: "24px 16px", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"}}>
      <h1 style={{margin: "0 0 4px", fontSize: "28px"}}>🌍 World Timezones — Current Time</h1>
      <p style={{margin: "0 0 20px", color: "#888", fontSize: "14px"}}>
        Live current time across {TIMEZONES.length} major timezones worldwide
      </p>

      <input
        type="text"
        placeholder="Search country, city, or timezone..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: "100%",
          padding: "12px 16px",
          borderRadius: "10px",
          border: "1px solid #ddd",
          fontSize: "15px",
          marginBottom: "20px",
          outline: "none",
          boxSizing: "border-box",
        }}
      />

      <PayToUnlock
        title="World Timezones — Unlock"
        toAddr="0x8fd379246834eac74B8419FfdA202CF8051F7A03"
        chain="pol"
        uid="world-timezones"
        freeTrial={{period: 60 * 60 * 1000, text: "Free Trial 1 Hour"}}
        confs={[
          {period: 30 * 24 * 60 * 60 * 1000, price: 1, text: "$1 / month"},
          {period: 365 * 24 * 60 * 60 * 1000, price: 10, text: "$10 / year"},
        ]}
        // pure frontend price fetch (static site has no /api/get-crypto-price backend route)
        getCryptoPrice={async (symbol) => {
          try {
            const rsp = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=polygon-ecosystem-token&vs_currencies=usd`)
            const data = await rsp.json()
            return data["polygon-ecosystem-token"]?.usd ?? null
          } catch {
            return null
          }
        }}
      >
        <TimezoneTable timezones={filtered} />
      </PayToUnlock>
    </div>
  );
}
